import React from 'react';
import { Box } from '@mui/material';
import { useLayout } from '../context/LayoutContext';
import { useDesign } from '../context/DesignContext';
import { useTypography } from '../context/TypographyContext';
import { useStyle } from '../context/StyleContext';
import { useBackground } from '../context/BackgroundContext';
import { useSpecialEffect } from '../context/SpecialEffectContext';
import { useBarStyle } from '../context/BarStyleContext';
import { getLayoutComponent } from './LayoutRegistry';
import { BaseLayout } from './BaseLayout';

/**
 * LayoutResolver component dynamically renders the selected layout
 * based on the current layout setting from LayoutContext
 */
const LayoutResolver = ({ 
  formData, 
  selectedSections, 
  currentPage = 0, 
  totalPages = 1,
  measureMode = false, 
  isPdfExport = false,
  onRenderComplete,
  // Optional direct props for use in PDF export or other scenarios
  selectedLayout: directSelectedLayout,
  activeDesign: directActiveDesign,
  typographySettings: directTypographySettings,
  selectedStyle: directSelectedStyle,
  activeStyle: directActiveStyle,
  selectedBackground: directSelectedBackground,
  activeBackgroundComponent: directActiveBackgroundComponent,
  selectedSpecialEffectId: directSelectedSpecialEffectId,
  activeBarStyle: directActiveBarStyle,
}) => {
  // Use context values unless direct props are provided
  const layoutContext = useLayout();
  const designContext = useDesign();
  const typographyContext = useTypography();
  const styleContext = useStyle();
  const backgroundContext = useBackground();
  const specialEffectContext = useSpecialEffect();
  const barStyleContext = useBarStyle();
  
  // Resolve all values with direct props taking precedence over context
  const selectedLayout = directSelectedLayout || layoutContext.selectedLayout;
  const activeDesign = directActiveDesign || designContext.activeDesign;
  const typographySettings = directTypographySettings || typographyContext.typographySettings;
  const selectedStyleId = directSelectedStyle || styleContext.selectedStyleId;
  const activeStyle = directActiveStyle || styleContext.activeStyle;
  const selectedBackgroundId = directSelectedBackground || backgroundContext.selectedBackgroundId;
  const activeBackgroundComponent = directActiveBackgroundComponent || backgroundContext.activeBackgroundComponent;
  const selectedSpecialEffectId = directSelectedSpecialEffectId || specialEffectContext.selectedSpecialEffectId;
  const activeBarStyle = directActiveBarStyle || barStyleContext.activeBarStyle;
  
  // Get additional context values that might be needed
  const { selectedDesign, customColorScheme } = designContext;

  // Get the selected layout component 
  const SelectedLayout = getLayoutComponent(selectedLayout);

  const calculatedTypographyStyles = BaseLayout.getTypographyStyles(typographySettings, activeDesign);
  const typographyKey = BaseLayout.getTypographyKey(typographySettings);
  const customSchemeKeyPart = selectedDesign === 'custom'
    ? `-scheme-${JSON.stringify(customColorScheme)}` // Stringify to capture changes
    : '';
  const layoutKey = `layout-${selectedLayout}-${typographyKey}-style-${selectedStyleId}-bg-${selectedBackgroundId}-effect-${selectedSpecialEffectId}${customSchemeKeyPart}-barStyle-${activeBarStyle?.id}`;

  console.log('LayoutResolver rendering:', { layoutKey, isPdfExport });

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Render background component FIRST if it exists and background is not blank */}
      {activeBackgroundComponent && selectedBackgroundId !== 'blank' && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          {React.createElement(activeBackgroundComponent, {
            colors: activeDesign?.colors,
            renderWidth: 210 // A4 width in mm
          })}
        </Box>
      )}
      
      {/* Render the actual CV content */}
      <Box sx={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent' }}>
        <SelectedLayout
          key={layoutKey}
          formData={formData}
          selectedSections={selectedSections}
          design={activeDesign}
          typographySettings={typographySettings}
          typographyStyles={calculatedTypographyStyles}
          selectedStyle={selectedStyleId}
          activeStyle={activeStyle}
          selectedBackground={selectedBackgroundId}
          activeBackgroundComponent={activeBackgroundComponent}
          selectedSpecialEffectId={selectedSpecialEffectId}
          activeBarStyle={activeBarStyle}
          currentPage={currentPage}
          totalPages={totalPages}
          measureMode={measureMode}
          isPdfExport={isPdfExport}
          onRenderComplete={onRenderComplete}
        />
      </Box>
    </Box>
  );
};

export default LayoutResolver; 